import React from "react";
import { Box, Typography, Autocomplete, TextField, createFilterOptions } from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { getIn, useFormikContext } from "formik";

const defaultFilterOptions = createFilterOptions();

const isSelectOption = (o) => {
    const l = String(o?.label || o?.Name || o?.name || o?.DisplayText || o?.Value || o?.corpName || o?.PayrollItemName || o || "").toLowerCase();
    const v = o?.value ?? o?.ID ?? o?.id ?? o?.corpValue ?? o?.ItemID;
    return l.includes("select") || v === "" || v === null || v === undefined || v === "000000000000000000000000000000000000";
};

const isAddNewOption = (o) => {
    const l = String(o?.label || o?.Name || o?.name || o?.DisplayText || o?.Value || o?.corpName || o?.PayrollItemName || o || "").toLowerCase();
    const v = String(o?.value ?? o?.ID ?? o?.id ?? o?.ItemID ?? "").toUpperCase();
    return v === "ADD_NEW" || v === "ADD_NEW_EMPLOYEE" || v === "ADD_OTHER" || l.includes("add new");
};

export const Dropdown = ({
    label,
    name,
    value,
    options,
    getOptionLabel,
    isEqual,
    onChange,
    error,
    helperText,
    autoFocus,
    inputRef,
    disabled,
    sx,
    renderOption,
    popperSx,
    onBlur,
    loading,
    onKeyDown,
    title,
}) => {
    const [highlightedOption, setHighlightedOption] = React.useState(null);
    const [open, setOpen] = React.useState(false);
    const filteredOptionsRef = React.useRef([]);
    const hasUserTypedRef = React.useRef(false);
    const highlightReasonRef = React.useRef(null);

    const getLabel = (opt) => {
        if (!opt) return "";
        if (getOptionLabel) return getOptionLabel(opt);
        return opt?.label || opt?.Name || opt?.name || opt?.DisplayText || String(opt);
    };

    const handleSelectOption = (optionToSelect) => {
        if (optionToSelect) {
            onChange(name, optionToSelect);
        }
        setOpen(false);
        setHighlightedOption(null);
        hasUserTypedRef.current = false;
        highlightReasonRef.current = null;
    };

    const handleInternalKeyDown = (e) => {
        if (e.key === "Tab") {
            if (hasUserTypedRef.current) {
                const firstMatch = filteredOptionsRef.current?.find(o => !isSelectOption(o) && !isAddNewOption(o));
                const optionToSelect = (highlightReasonRef.current === "keyboard" ? highlightedOption : null) || firstMatch || highlightedOption || (filteredOptionsRef.current && filteredOptionsRef.current[0]);
                if (optionToSelect) {
                    handleSelectOption(optionToSelect);
                } else {
                    setOpen(false);
                    setHighlightedOption(null);
                    hasUserTypedRef.current = false;
                    highlightReasonRef.current = null;
                }
            } else if (highlightReasonRef.current === "keyboard" && highlightedOption) {
                handleSelectOption(highlightedOption);
            } else {
                setOpen(false);
                setHighlightedOption(null);
                hasUserTypedRef.current = false;
                highlightReasonRef.current = null;
            }
        } else if (e.key === "Enter") {
            e.preventDefault();
            if (hasUserTypedRef.current) {
                const firstMatch = filteredOptionsRef.current?.find(o => !isSelectOption(o) && !isAddNewOption(o));
                const optionToSelect = (highlightReasonRef.current === "keyboard" ? highlightedOption : null) || firstMatch || highlightedOption || (filteredOptionsRef.current && filteredOptionsRef.current[0]);
                if (optionToSelect) {
                    handleSelectOption(optionToSelect);
                }
            } else if (highlightReasonRef.current === "keyboard" && highlightedOption) {
                handleSelectOption(highlightedOption);
            } else if (open) {
                const optionToSelect = highlightedOption || (filteredOptionsRef.current && filteredOptionsRef.current[0]);
                if (optionToSelect) {
                    handleSelectOption(optionToSelect);
                }
            }
        }
    };

    const dropdownOptions = React.useMemo(() => {
        const opts = options || [];
        if (!Array.isArray(opts) || opts.length === 0) return opts;
        return opts.some(isSelectOption) ? opts : [{ label: "--Select--", value: "" }, ...opts];
    }, [options]);

    const selectedLabel = value ? getLabel(value) : "";
    const tooltipText = selectedLabel || title || undefined;

    return (
    <Box width="100%" title={tooltipText}>
        {label && (
            <Typography color="text.secondary" fontSize="11px" fontWeight={500} mb={0.5}>
                {label}
            </Typography>
        )}

        <Autocomplete
            size="small"
            open={open}
            onOpen={() => setOpen(true)}
            onClose={() => {
                setOpen(false);
                setHighlightedOption(null);
                hasUserTypedRef.current = false;
                highlightReasonRef.current = null;
            }}
            onInputChange={(event, newInputValue, reason) => {
                if (reason === "input") {
                    hasUserTypedRef.current = true;
                } else if (reason === "reset" || reason === "clear") {
                    hasUserTypedRef.current = false;
                }
            }}
            disableClearable
            disabled={disabled}
            loading={loading}
            options={dropdownOptions}
            value={value}
            onChange={(e, newValue) => {
                setHighlightedOption(null);
                hasUserTypedRef.current = false;
                highlightReasonRef.current = null;
                onChange(name, newValue);
            }}
            getOptionLabel={getOptionLabel || getLabel}
            onHighlightChange={(event, option, reason) => {
                if (reason !== "mouse") {
                    setHighlightedOption(option);
                    highlightReasonRef.current = reason;
                }
            }}
            filterOptions={(opts, state) => {
                const filtered = defaultFilterOptions(opts, state);
                const query = (state?.inputValue || "").trim();
                if (!query) {
                    filteredOptionsRef.current = filtered;
                    return filtered;
                }
                const selectOpt = opts.find(isSelectOption);
                const addNewOpt = opts.find(isAddNewOption);
                const matchingItems = filtered.filter(
                    (o) => !isSelectOption(o) && !isAddNewOption(o)
                );
                const result = [];
                if (selectOpt) result.push(selectOpt);
                if (addNewOpt) result.push(addNewOpt);
                result.push(...matchingItems);
                filteredOptionsRef.current = result;
                return result;
            }}
            autoHighlight
            isOptionEqualToValue={isEqual || ((option, val) => {
                if (!option || !val) return false;
                const optId = option?.ID ?? option?.id ?? option?.value ?? option?.ValueCode ?? option;
                const valId = val?.ID ?? val?.id ?? val?.value ?? val?.ValueCode ?? val;
                return String(optId) === String(valId);
            })}
            renderOption={(props, option, state) => {
                if (renderOption) {
                    const rendered = renderOption(props, option, state);
                    if (React.isValidElement(rendered)) {
                        const optLabel = getLabel(option);
                        return React.cloneElement(rendered, {
                            title: rendered.props.title || optLabel || undefined,
                        });
                    }
                    return rendered;
                }
                const { key, ...restProps } = props;
                const uniqueKey = option?.ID ?? option?.id ?? option?.value ?? option?.corpValue ?? option?.ValueCode ?? `${getLabel(option)}-${state?.index ?? ''}`;
                const optionLabel = getLabel(option);
                const isAdd = isAddNewOption(option);
                return (
                    <li key={uniqueKey} {...restProps} title={optionLabel} style={{ ...(isAdd ? { color: "#d32f2f", fontWeight: 600 } : {}) }}>
                        <span title={optionLabel} style={{ width: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {optionLabel}
                        </span>
                    </li>
                );
            }}
            slotProps={{
                popper: {
                    placement: "bottom-start",
                    modifiers: [{ name: "flip", enabled: true }],
                    sx: popperSx,
                },
            }}
            ListboxProps={{
                sx: {
                    maxHeight: 200,
                    overflowY: "auto",
                    "& .MuiAutocomplete-option": {
                        fontSize: "0.7rem",
                        lineHeight: "18px",
                        paddingTop: "5px",
                        paddingBottom: "5px",
                        paddingLeft: "8px",
                        paddingRight: "8px",
                        borderBottom: "1px solid #d8d8d8",
                    },
                    "& .MuiAutocomplete-option:last-child": {
                        borderBottom: "none",
                    },
                    "& .MuiAutocomplete-option[aria-selected='true']": {
                        backgroundColor: "#2f5d8a !important",
                        color: "white",
                    },
                    "&::-webkit-scrollbar": {
                        width: 6,
                    },
                    "&::-webkit-scrollbar-thumb": {
                        backgroundColor: "#bdbdbd",
                        borderRadius: 10,
                    },
                    "&::-webkit-scrollbar-thumb:hover": {
                        backgroundColor: "#9e9e9e",
                    },
                },
            }}
            popupIcon={<ArrowDropDownIcon fontSize="small" />}
            renderInput={(params) => {
                const currentInputValue = params.inputProps?.value || selectedLabel || title || "";
                return (
                    <span title={currentInputValue || undefined} style={{ width: "100%", display: "inline-block" }}>
                        <TextField
                            {...params}
                            title={currentInputValue || undefined}
                            onKeyDown={(e) => {
                                if (onKeyDown) {
                                    onKeyDown(e);
                                }
                                if (!e.defaultPrevented) {
                                    handleInternalKeyDown(e);
                                }
                            }}
                            autoFocus={autoFocus}
                            inputRef={inputRef}
                            onBlur={(e) => {
                                hasUserTypedRef.current = false;
                                highlightReasonRef.current = null;
                                if (onBlur) onBlur(e);
                            }}
                            variant="filled"
                            error={error}
                            inputProps={{
                                ...params.inputProps,
                                title: currentInputValue || undefined,
                            }}
                            sx={[
                                {
                                    width: "100%",
                                    "& .MuiFilledInput-root": {
                                        height: 32,
                                        padding: 0,
                                        backgroundColor: disabled ? "#E0E0E0" : "white",
                                        "&:hover": { backgroundColor: disabled ? "#E0E0E0" : "white" },
                                        "&.Mui-focused": { backgroundColor: disabled ? "#E0E0E0" : "white" },
                                        "&.Mui-disabled": {
                                            backgroundColor: "#E0E0E0!important",
                                        },
                                    },
                                    "& .MuiInputBase-input": {
                                        fontSize: "11px",  
                                         "&.Mui-disabled": {
                                            WebkitTextFillColor: "#000 !important",
                                            color: "#000 !important",
                                            cursor: "not-allowed",
                                            backgroundColor: "#E0E0E0 !important",

                                        },    // Placeholder & selected text
                                    },
                                    "& .MuiAutocomplete-input": {
                                        fontSize: "11px",      // Typed text
                                    },
                                    "& .MuiFormHelperText-root": {
                                        fontSize: "0.7rem",      // Helper/error text
                                    },
                                },
                                ...(Array.isArray(sx) ? sx : [sx]),
                            ]}
                        />
                    </span>
                );
            }}
        />

        {error && (
            <Typography color="error" variant="caption">
                {helperText}
            </Typography>
        )}
    </Box>
    );
};

export const PayrollGridDropdown = ({ name, options, onAddNew, showErrorPopup }) => {
    const { values, setFieldValue, errors, touched } = useFormikContext();
    const rawValue = getIn(values, name);
    const error = getIn(errors, name);
    const isTouched = getIn(touched, name);
    const showError = isTouched && Boolean(error);

    const selectedValue = options.find((opt) => opt.value === rawValue) || null;

    return (
        <Autocomplete
            freeSolo
            size="small"
            forcePopupIcon={true}
            disableClearable
            options={options}
            value={selectedValue}
            onChange={(_, newValue) => {
                const selected = typeof newValue === "string" ? newValue : newValue ? newValue.value : "";

                if (selected === "ADD_NEW") {
                    if (onAddNew) onAddNew();
                    return;
                }

                if (name.includes("expenseCoa")) {
                    const payableValue = getIn(values, name.replace("expenseCoa", "payableCoa"));
                    if (selected && selected === payableValue) {
                        showErrorPopup("Expense COA and Payable COA cannot be the same.");
                        return;
                    }
                }

                if (name.includes("payableCoa")) {
                    const expenseValue = getIn(values, name.replace("payableCoa", "expenseCoa"));
                    if (selected && selected === expenseValue) {
                        showErrorPopup("Expense COA and Payable COA cannot be the same.");
                        return;
                    }
                }

                setFieldValue(name, selected);
            }}
            getOptionLabel={(option) => option?.label || option?.Name || ""}
            isOptionEqualToValue={(option, value) => option?.value === value?.value || (option?.value != null && value?.value != null && String(option.value) === String(value.value))}
            renderOption={(props, option, state) => {
                const { key, ...restProps } = props;
                const uniqueKey = option?.value ?? `${option?.label || "opt"}-${state?.index ?? ""}`;
                const optionLabel = option?.label || option?.Name || (typeof option === "string" ? option : "");
                const isAddNew = option?.value === "ADD_NEW";
                return (
                    <li key={uniqueKey} {...restProps} title={optionLabel} style={{ ...(isAddNew ? { color: "#d32f2f", fontWeight: 600 } : {}) }}>
                        <span title={optionLabel} style={{ width: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {optionLabel}
                        </span>
                    </li>
                );
            }}
            renderInput={(params) => {
                const selectedLabel = selectedValue?.label || selectedValue?.Name || (typeof selectedValue === "string" ? selectedValue : "") || "";
                const currentInputValue = params.inputProps?.value ?? selectedLabel ?? "";
                return (
                    <span title={currentInputValue || undefined} style={{ width: "100%", display: "inline-block" }}>
                        <TextField
                            {...params}
                            title={currentInputValue || undefined}
                            inputProps={{
                                ...params.inputProps,
                                title: currentInputValue || undefined,
                            }}
                            error={showError}
                            helperText={showError ? error : ""}
                            variant="filled"
                            size="small"
                            sx={{
                                ".MuiInputBase-root": {
                                    paddingTop: 0,
                                    paddingBottom: 0,
                                    backgroundColor: "white",
                                    "&:hover": { backgroundColor: "white" },
                                    "&.Mui-focused": { backgroundColor: "white" },
                                },
                            }}
                        />
                    </span>
                );
            }}
        />
    );
};
